// Circular buffer in-memoria per gli ultimi errori console e fetch fallite.
// Usato da debug-snapshot.ts per allegare contesto utile ai ticket di supporto.
//
// Inizializzato una sola volta dal main.tsx via installDebugBuffer().
// Niente dati sensibili: troncoamo body fetch a 200 char e omettiamo gli
// header. Stack trace dei console.error sono testuali e tagliati a 2KB.

const CONSOLE_MAX = 25;
const NETWORK_MAX = 15;
const STACK_MAX_CHARS = 2000;
const BODY_MAX_CHARS = 200;

export type ConsoleEntry = {
  ts: string;
  level: "error" | "warn";
  message: string;
};

export type NetworkEntry = {
  ts: string;
  method: string;
  url: string;
  status: number | null;
  ok: boolean;
  duration_ms: number;
  error?: string;
};

const consoleBuf: ConsoleEntry[] = [];
const networkBuf: NetworkEntry[] = [];

function pushConsole(level: "error" | "warn", args: unknown[]) {
  const parts: string[] = [];
  for (const a of args) {
    if (a instanceof Error) {
      const stack = (a.stack || `${a.name}: ${a.message}`).slice(0, STACK_MAX_CHARS);
      parts.push(stack);
    } else if (typeof a === "object") {
      try { parts.push(JSON.stringify(a)); } catch { parts.push(String(a)); }
    } else {
      parts.push(String(a));
    }
  }
  consoleBuf.push({
    ts: new Date().toISOString(),
    level,
    message: parts.join(" ").slice(0, STACK_MAX_CHARS),
  });
  while (consoleBuf.length > CONSOLE_MAX) consoleBuf.shift();
}

function pushNetwork(entry: NetworkEntry) {
  networkBuf.push(entry);
  while (networkBuf.length > NETWORK_MAX) networkBuf.shift();
}

let installed = false;
export function installDebugBuffer() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // console.error / console.warn passthrough + cattura
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...args: unknown[]) => {
    try { pushConsole("error", args); } catch { /* noop */ }
    origError(...args);
  };
  console.warn = (...args: unknown[]) => {
    try { pushConsole("warn", args); } catch { /* noop */ }
    origWarn(...args);
  };

  // window.onerror per errori non-loggati esplicitamente
  window.addEventListener("error", (e) => {
    try {
      pushConsole("error", [`window.onerror: ${e.message || "—"}`, e.error || ""]);
    } catch { /* noop */ }
  });
  window.addEventListener("unhandledrejection", (e) => {
    try {
      pushConsole("error", [`unhandledrejection: ${String(e.reason).slice(0, BODY_MAX_CHARS)}`]);
    } catch { /* noop */ }
  });

  // fetch wrapper: registra status + durata
  const origFetch = window.fetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const start = performance.now();
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = (init?.method || (typeof input !== "string" && !(input instanceof URL) ? input.method : "GET")).toUpperCase();
    try {
      const res = await origFetch(input, init);
      const dur = performance.now() - start;
      if (!res.ok) {
        pushNetwork({
          ts: new Date().toISOString(),
          method, url, status: res.status, ok: false,
          duration_ms: Math.round(dur),
        });
      }
      return res;
    } catch (err: any) {
      const dur = performance.now() - start;
      pushNetwork({
        ts: new Date().toISOString(),
        method, url, status: null, ok: false,
        duration_ms: Math.round(dur),
        error: String(err?.message || err).slice(0, BODY_MAX_CHARS),
      });
      throw err;
    }
  };
}

export function getConsoleBuffer(): readonly ConsoleEntry[] {
  return [...consoleBuf];
}
export function getNetworkBuffer(): readonly NetworkEntry[] {
  return [...networkBuf];
}
